import { type ReactNode } from 'react';
import { 
  Users, 
  UserPlus, 
  Receipt, 
  Wallet,
  TrendingUp,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { useAuth, DEPARTMENT_CONFIG } from '@/contexts/AuthContext';
import { formatCurrency, formatRelativeTime } from '@/lib/utils';

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: ReactNode;
  iconBg?: string;
}

function StatCard({ title, value, change, changeLabel, icon, iconBg = 'bg-pond/10' }: StatCardProps) {
  const isPositive = change !== undefined && change >= 0;
  
  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-chalkboard dark:text-rain-cloud">{title}</p>
            <p className="text-2xl font-semibold text-slate dark:text-mist mt-1">
              {value}
            </p>
            {change !== undefined && (
              <div className="flex items-center gap-1 mt-2">
                {isPositive ? (
                  <ArrowUpRight className="w-4 h-4 text-turquoise dark:text-aqua" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-rust dark:text-salmon" />
                )}
                <span className={isPositive ? 'text-turquoise dark:text-aqua' : 'text-rust dark:text-salmon'}>
                  {Math.abs(change)}%
                </span>
                <span className="text-xs text-chalkboard dark:text-rain-cloud">
                  {changeLabel || 'vs last month'}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${iconBg}`}>
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
}

// Recent Activity Item
interface ActivityItemProps {
  title: string;
  description: string;
  time: Date;
  type: 'client' | 'invoice' | 'feature' | 'communication';
}

function ActivityItem({ title, description, time, type }: ActivityItemProps) {
  const iconMap = {
    client: <Users className="w-4 h-4" />,
    invoice: <Receipt className="w-4 h-4" />,
    feature: <TrendingUp className="w-4 h-4" />,
    communication: <Clock className="w-4 h-4" />,
  };

  const bgMap = {
    client: 'bg-pond/10 text-pond dark:text-pond-light',
    invoice: 'bg-turquoise/10 text-turquoise dark:text-aqua',
    feature: 'bg-rust/10 text-rust dark:text-salmon',
    communication: 'bg-yellow/10 text-earth dark:text-yellow-light',
  };

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray dark:border-night-pond last:border-0">
      <div className={`p-2 rounded-lg ${bgMap[type]}`}>
        {iconMap[type]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate dark:text-mist truncate">
          {title}
        </p>
        <p className="text-xs text-chalkboard dark:text-rain-cloud">
          {description}
        </p>
      </div>
      <span className="text-xs text-french-gray whitespace-nowrap">
        {formatRelativeTime(time)}
      </span>
    </div>
  );
}

// Quick Action Button
interface QuickActionProps {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
}

function QuickAction({ icon, label, onClick }: QuickActionProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 rounded-lg hover:bg-pale-gray-2 dark:hover:bg-puddle/20 transition-colors"
    >
      <div className="p-3 rounded-full bg-pond/10 text-pond dark:text-pond-light">
        {icon}
      </div>
      <span className="text-sm text-slate dark:text-mist">{label}</span>
    </button>
  );
}

export function Dashboard() {
  const { user } = useAuth();
  const departmentLabel = user ? DEPARTMENT_CONFIG[user.primaryDepartment].label : '';

  // Mock data for dashboard
  const stats = {
    activeClients: 48,
    activeClientsChange: 12,
    prospects: 15,
    prospectsChange: 8,
    pendingInvoices: formatCurrency(125000),
    pendingInvoicesChange: -5,
    receivables: formatCurrency(45000),
    receivablesChange: 15,
  };

  const recentActivity: ActivityItemProps[] = [
    {
      title: 'First National Bank',
      description: 'Feature flags updated by John Doe',
      time: new Date(Date.now() - 30 * 60 * 1000), // 30 mins ago
      type: 'feature',
    },
    {
      title: 'Community Credit Union',
      description: 'Invoice #INV-2024-001 sent',
      time: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      type: 'invoice',
    },
    {
      title: 'Metro Bank & Trust',
      description: 'Onboarding completed',
      time: new Date(Date.now() - 5 * 60 * 60 * 1000), // 5 hours ago
      type: 'client',
    },
    {
      title: 'Summit Financial',
      description: 'Call logged - quarterly review',
      time: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
      type: 'communication',
    },
    {
      title: 'Valley State Bank',
      description: 'New prospect added',
      time: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      type: 'client',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate dark:text-mist">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-chalkboard dark:text-rain-cloud mt-1">
          {departmentLabel} Dashboard • Here&apos;s what&apos;s happening today
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Active Clients"
          value={stats.activeClients}
          change={stats.activeClientsChange}
          icon={<Users className="w-6 h-6 text-pond dark:text-pond-light" />}
          iconBg="bg-pond/10"
        />
        <StatCard
          title="Prospects"
          value={stats.prospects}
          change={stats.prospectsChange}
          icon={<UserPlus className="w-6 h-6 text-turquoise dark:text-aqua" />}
          iconBg="bg-turquoise/10"
        />
        <StatCard
          title="Pending Invoices"
          value={stats.pendingInvoices}
          change={stats.pendingInvoicesChange}
          icon={<Receipt className="w-6 h-6 text-rust dark:text-salmon" />}
          iconBg="bg-rust/10"
        />
        <StatCard
          title="Total Receivables"
          value={stats.receivables}
          change={stats.receivablesChange}
          icon={<Wallet className="w-6 h-6 text-yellow dark:text-yellow-light" />}
          iconBg="bg-yellow/10"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate dark:text-mist">
              Recent Activity
            </h2>
          </div>
          <div className="card-body">
            {recentActivity.map((activity, index) => (
              <ActivityItem key={index} {...activity} />
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate dark:text-mist">
              Quick Actions
            </h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-2">
              <QuickAction
                icon={<UserPlus className="w-5 h-5" />}
                label="Add Client"
              />
              <QuickAction
                icon={<Receipt className="w-5 h-5" />}
                label="New Invoice"
              />
              <QuickAction
                icon={<Clock className="w-5 h-5" />}
                label="Log Call"
              />
              <QuickAction
                icon={<TrendingUp className="w-5 h-5" />}
                label="View Reports"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
